import { create } from 'zustand';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Check if a JWT is expired by decoding the payload.
 * Returns true if token is missing, malformed, or expired.
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 < Date.now() : false;
  } catch {
    return true;
  }
};

const storedToken = localStorage.getItem('token');

export const useAuthStore = create((set, get) => ({
  user: null,
  token: isTokenExpired(storedToken) ? null : storedToken,
  loading: true,

  init: async () => {
    const token = get().token;
    if (token) {
      if (isTokenExpired(token)) {
        get().logout();
        return;
      }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        set({ user: res.data, loading: false });
      } catch {
        get().logout();
      }
    } else {
      localStorage.removeItem('token');
      set({ loading: false });
    }
  },

  login: async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user } = res.data;
    localStorage.setItem('token', access_token);
    set({ token: access_token, user });
    return user;
  },

  register: async (name, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password });
    const { access_token, user } = res.data;
    localStorage.setItem('token', access_token);
    set({ token: access_token, user });
    return user;
  },

  logout: () => {
    localStorage.removeItem('token');
    set({ token: null, user: null, loading: false });
  },
}));

// Backward-compatible hook (same interface as old AuthContext)
export const useAuth = () => {
  const store = useAuthStore();
  return store;
};
